import type { CategoryRequest, CategoryRequestStatus, Vendor } from '@shopgnd/shared';
import { createCategory, listCategories } from '@/api/categories';
import { nextSortOrderForParent } from '@/lib/categoryTree';
import { getAuthedDataClient, getCatalogClient } from '@/lib/amplify-client';
import { slugifyVendorName } from '@/lib/vendorUrl';

const nowIso = () => new Date().toISOString();

function slugifyCategoryName(name: string): string {
  return slugifyVendorName(name) || 'category';
}

function mapCategoryRequest(row: Record<string, unknown>): CategoryRequest {
  return {
    id: String(row.id),
    vendorId: String(row.vendorId),
    vendorName: String(row.vendorName),
    requestedByUserId: String(row.requestedByUserId),
    name: String(row.name),
    slug: String(row.slug),
    parentId: row.parentId ? String(row.parentId) : undefined,
    status: String(row.status) as CategoryRequestStatus,
    adminNote: row.adminNote ? String(row.adminNote) : undefined,
    resolvedCategoryId: row.resolvedCategoryId ? String(row.resolvedCategoryId) : undefined,
    createdAt: String(row.createdAt ?? nowIso()),
    updatedAt: String(row.updatedAt ?? nowIso()),
  };
}

export type ResolveCategoryRequestAction =
  | { type: 'approve' }
  | { type: 'assign'; categoryId: string; note?: string }
  | { type: 'reject'; note?: string };

export async function createCategoryRequest(
  vendor: Pick<Vendor, 'id' | 'businessName'>,
  requestedByUserId: string,
  name: string,
): Promise<CategoryRequest> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Category name is required');

  const slug = slugifyCategoryName(trimmed);

  const client = await getAuthedDataClient();
  const existing = await client.models.CategoryRequest.list({
    filter: {
      and: [
        { vendorId: { eq: vendor.id } },
        { status: { eq: 'pending' } },
        { slug: { eq: slug } },
      ],
    },
  });
  const found = (existing.data ?? [])[0];
  if (found) return mapCategoryRequest(found as Record<string, unknown>);

  const res = await client.models.CategoryRequest.create({
    vendorId: vendor.id,
    vendorName: vendor.businessName,
    requestedByUserId,
    name: trimmed,
    slug,
    status: 'pending',
  });
  if (!res.data) throw new Error('Could not create category request');
  return mapCategoryRequest(res.data as Record<string, unknown>);
}

export async function getCategoryRequest(id: string): Promise<CategoryRequest | null> {
  try {
    const client = await getCatalogClient();
    const res = await client.models.CategoryRequest.get({ id });
    return res.data ? mapCategoryRequest(res.data as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export async function listCategoryRequestsForVendor(vendorId: string): Promise<CategoryRequest[]> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.CategoryRequest.list({
      filter: { vendorId: { eq: vendorId } },
    });
    return (res.data ?? []).map((row: Record<string, unknown>) => mapCategoryRequest(row));
  } catch {
    return [];
  }
}

export async function listPendingCategoryRequestsAdmin(): Promise<CategoryRequest[]> {
  try {
    const client = await getAuthedDataClient();
    const res = await client.models.CategoryRequest.list({
      filter: { status: { eq: 'pending' } },
    });
    return (res.data ?? []).map((row: Record<string, unknown>) => mapCategoryRequest(row));
  } catch {
    return [];
  }
}

async function listProductIdsByCategoryRequestId(categoryRequestId: string): Promise<string[]> {
  const client = await getAuthedDataClient();
  const res = await client.models.Product.list({
    filter: { categoryRequestId: { eq: categoryRequestId } },
  });
  return (res.data ?? []).map((row: Record<string, unknown>) => String(row.id));
}

export async function resolveCategoryRequestAdmin(
  requestId: string,
  action: ResolveCategoryRequestAction,
): Promise<CategoryRequest | null> {
  const request = await getCategoryRequest(requestId);
  if (!request || request.status !== 'pending') return null;

  const productIds = await listProductIdsByCategoryRequestId(requestId);
  const { updateProductListingAdmin } = await import('@/api/catalog');

  if (action.type === 'approve') {
    const all = await listCategories();
    const category = await createCategory({
      name: request.name,
      slug: request.slug,
      parentId: request.parentId,
      sortOrder: nextSortOrderForParent(all, request.parentId),
    });
    for (const productId of productIds) {
      await updateProductListingAdmin(productId, {
        categoryId: category.id,
        listingStatus: 'published',
        categoryRequestId: '',
        listingNote: undefined,
      });
    }
    return updateCategoryRequestRecord(requestId, {
      status: 'approved',
      resolvedCategoryId: category.id,
    });
  }

  if (action.type === 'assign') {
    for (const productId of productIds) {
      await updateProductListingAdmin(productId, {
        categoryId: action.categoryId,
        listingStatus: 'published',
        categoryRequestId: '',
        listingNote: action.note,
      });
    }
    return updateCategoryRequestRecord(requestId, {
      status: 'approved',
      resolvedCategoryId: action.categoryId,
      adminNote: action.note,
    });
  }

  for (const productId of productIds) {
    await updateProductListingAdmin(productId, {
      listingStatus: 'draft',
      listingNote: action.note,
    });
  }
  return updateCategoryRequestRecord(requestId, {
    status: 'rejected',
    adminNote: action.note,
  });
}

async function updateCategoryRequestRecord(
  id: string,
  patch: Partial<Pick<CategoryRequest, 'status' | 'adminNote' | 'resolvedCategoryId'>>,
): Promise<CategoryRequest | null> {
  const client = await getAuthedDataClient();
  const res = await client.models.CategoryRequest.update({ id, ...patch });
  return res.data ? mapCategoryRequest(res.data as Record<string, unknown>) : null;
}
